import { SelectPaginateAsync } from "@/components/atoms/Select";
import UserRoleFilter, {
  canFilterBySelf,
} from "@/components/molecules/UserRoleFilter";
import useLoadOptions from "@/hooks/useLoadOptions";
import User from "@/interfaces/IUser";
import MontageMeetingStatus from "@/interfaces/MontageMeetingStatus";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { FC } from "react";
import { Button, Form, Offcanvas } from "react-bootstrap";
import { useSelector } from "react-redux";
import { MultiValue } from "react-select";

/**
 * Panneau « Filtres » du calendrier montage. Il ne reprend pas celui du
 * calendrier commercial : le montage ne filtre que sur ce qui organise son
 * travail, le statut du rendez-vous et les personnes qui le portent.
 */
export interface MontageCalendarFilters {
  montageStatuses?: MultiValue<MontageMeetingStatus>;
  unknownMontageStatus?: boolean;
  aes?: MultiValue<User>;
  sdrs?: MultiValue<User>;
  csms?: MultiValue<User>;
}

/** Valeurs posées par « Réinitialiser ». */
export const EMPTY_MONTAGE_CALENDAR_FILTERS: MontageCalendarFilters = {
  montageStatuses: [],
  unknownMontageStatus: false,
  aes: [],
  sdrs: [],
  csms: [],
};

export const countActiveMontageCalendarFilters = (
  filters: MontageCalendarFilters,
) =>
  (filters.montageStatuses?.length || 0) +
  (filters.unknownMontageStatus ? 1 : 0) +
  (filters.aes?.length || 0) +
  (filters.sdrs?.length || 0) +
  (filters.csms?.length || 0);

/** Paramètres envoyés à GET /montage-meetings. */
export const toMontageCalendarParams = (filters: MontageCalendarFilters) => ({
  ...(filters.montageStatuses?.length && {
    montageStatusIds: filters.montageStatuses.map((status) => status.id),
  }),
  ...(filters.unknownMontageStatus && { unknownMontageStatus: true }),
  ...(filters.aes?.length && {
    aeIds: filters.aes.map((user) => user.id),
  }),
  ...(filters.sdrs?.length && {
    sdrIds: filters.sdrs.map((user) => user.id),
  }),
  ...(filters.csms?.length && {
    csmOwnerIds: filters.csms.map((user) => user.id),
  }),
});

interface MontageCalendarFiltersPanelProps {
  show: boolean;
  onHide: () => void;
  filters: MontageCalendarFilters;
  onFiltersChange: (changes: Partial<MontageCalendarFilters>) => void;
  /** Affiche les sélecteurs Commercial et Télépro. Sans cela, chacun reste
   *  affiché à qui a le rôle correspondant, pour se choisir soi-même. */
  showUserFilters: boolean;
  /** `all` : tous les utilisateurs. `pool` : l'entourage seulement
   *  (/users/calendar-filterable), comme sur le calendrier commercial. */
  userOptions: "all" | "pool";
}

export const MontageCalendarFiltersPanel: FC<
  MontageCalendarFiltersPanelProps
> = ({
  show,
  onHide,
  filters,
  onFiltersChange,
  showUserFilters,
  userOptions,
}) => {
  const currentUser = useSelector(selectCurrentUser);
  const { loadMontageMeetingStatusesOptions } = useLoadOptions();

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="dark__bg-card-dark"
    >
      <Offcanvas.Header closeButton className="bg-light">
        <h6 className="fs-0 mb-0 fw-semi-bold">Filtres</h6>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form>
          <Form.Group className="mt-n2">
            <Form.Label className="mb-1 fs--1">Statut montage :</Form.Label>
            <SelectPaginateAsync
              loadOptions={loadMontageMeetingStatusesOptions}
              additional={{ pageIndex: 0 }}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id.toString()}
              value={filters.montageStatuses}
              onChange={(montageStatuses: MultiValue<MontageMeetingStatus>) =>
                onFiltersChange({ montageStatuses })
              }
              isMulti
              isClearable
              placeholder="Sélectionner..."
            />
            <Form.Check
              checked={!!filters.unknownMontageStatus}
              onChange={() =>
                onFiltersChange({
                  unknownMontageStatus: !filters.unknownMontageStatus,
                })
              }
              label="Sans statut"
              className="my-2"
            />
          </Form.Group>
          {(showUserFilters || canFilterBySelf(currentUser, "AE")) && (
            <UserRoleFilter
              role="AE"
              source={userOptions}
              value={filters.aes}
              onChange={(aes) => onFiltersChange({ aes })}
            />
          )}
          {(showUserFilters || canFilterBySelf(currentUser, "SDR")) && (
            <UserRoleFilter
              role="SDR"
              source={userOptions}
              value={filters.sdrs}
              onChange={(sdrs) => onFiltersChange({ sdrs })}
            />
          )}
          {/* Tous les CSM, quel que soit le rôle : un créneau masqué montre
              déjà son CSM, et c'est ainsi qu'un commercial voit ses
              disponibilités. */}
          <UserRoleFilter
            role="CSM"
            source="all"
            value={filters.csms}
            onChange={(csms) => onFiltersChange({ csms })}
          />
          <Form.Group className="mt-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onFiltersChange(EMPTY_MONTAGE_CALENDAR_FILTERS)}
            >
              Réinitialiser
            </Button>
          </Form.Group>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};
